import { apiRequest } from '@nusrif/utils';
import {
  Cart,
  CartResponse,
  AddItemToCartRequest,
  AdjustCartItemRequest,
  SetShippingAddressRequest,
  SetShippingMethodRequest
} from '@nusrif/types';
import { getUser, getUserFromServer } from './user-services';
import { isAuthenticated } from './auth-services';

const cartLocalStorageKey = "shopping-cart";

export function setCart(cart: Cart) {
  localStorage.setItem(cartLocalStorageKey, JSON.stringify(cart));
}

export function getCart(): Cart | null {
  const cart = localStorage.getItem(cartLocalStorageKey);
  return cart ? JSON.parse(cart) : null;
}

export function removeCart() {
  localStorage.removeItem(cartLocalStorageKey);
}

/**
 * Create a new cart for the authenticated user
 */
export async function createCart(): Promise<CartResponse> {
  const response = await apiRequest<CartResponse>('/cart', {
    method: 'POST',
  });

  if (response.success && response.data) {
    setCart(response.data);

    // Sync user with cartId
    getUserFromServer();
  }

  return response;
}

/**
 * Get the current cart for the authenticated user
 */
export async function getCartFromServer(): Promise<CartResponse> {
  const response = await apiRequest<CartResponse>('/cart', {
    method: 'GET',
  });

  if (response.success && response.data) {
    setCart(response.data);
  }

  return response;
}

/**
 * Add an item to the authenticated user's cart
 */
export async function addItemToCart(params: AddItemToCartRequest): Promise<CartResponse> {
  const response = await apiRequest<CartResponse>('/cart', {
    method: 'PUT',
    body: params,
  });

  if (response.success && response.data) {
    setCart(response.data);
  }

  return response;
}

/**
 * Adjust the quantity or details of an item in the cart
 */
export async function adjustCartItem(params: AdjustCartItemRequest): Promise<CartResponse> {
  const response = await apiRequest<CartResponse>('/cart/adjust', {
    method: 'PUT',
    body: params,
  });

  if (response.success && response.data) {
    setCart(response.data);
  }

  return response;
}

/**
 * Set or update the shipping address for the cart
 */
export async function setShippingAddress(params: SetShippingAddressRequest): Promise<CartResponse> {
  const response = await apiRequest<CartResponse>('/cart/shipping-address', {
    method: 'PUT',
    body: params,
  });

  if (response.success && response.data) {
    setCart(response.data);
  }

  return response;
}

/**
 * Set or update the shipping method for the cart
 */
export async function setShippingMethod(params: SetShippingMethodRequest): Promise<CartResponse> {
  const response = await apiRequest<CartResponse>('/cart/shipping-method', {
    method: 'PUT',
    body: params,
  });

  if (response.success && response.data) {
    setCart(response.data);
  }

  return response;
}

/**
 * Remove an item from the cart
 */
export async function removeCartItem(lineItemId: string): Promise<CartResponse> {
  const response = await apiRequest<CartResponse>(`/cart/adjust`, {
    method: 'PUT',
    body: {
      orderLineId: lineItemId,
      quantity: 0
    }
  });

  if (response.success && response.data) {
    setCart(response.data);
  }

  return response;
}

/**
 * Clear all items from the cart
 */
export async function clearCart(): Promise<CartResponse> {
  const response = await apiRequest<CartResponse>('/cart/clear', {
    method: 'DELETE',
  });

  if (response.success && response.data) {
    setCart(response.data);
  }

  return response;
}

/**
 * Get cart summary (total items, total price, etc.)
 */
export function getCartSummary(cart: Cart) {
  const itemCount = cart.lines.reduce((total, line) => total + line.quantity, 0);
  const subtotal = cart.lines.reduce((total, line) => total + (line.unitPrice * line.quantity), 0) / 100; // Convert from cents

  return {
    itemCount,
    subtotal,
    total: cart.total / 100, // Convert from cents
    hasItems: itemCount > 0,
  };
}

/**
 * Check if cart has items
 */
export function hasCartItems(cart: Cart): boolean {
  return cart.lines.length > 0;
}

/**
 * Get total number of items in cart
 */
export function getCartItemCount(cart: Cart): number {
  return cart.lines.reduce((total, line) => total + line.quantity, 0);
}

/**
 * Get cart line by product ID
 */
export function getCartLineByProductId(cart: Cart, productId: string): Cart['lines'][0] | undefined {
  return cart.lines.find(line => line.productVariant.product.id === productId);
}

/**
 * Check if a product is already in the cart
 */
export function isProductInCart(cart: Cart, productId: string): boolean {
  return cart.lines.some(line => line.productVariant.product.id === productId);
}

/**
 * Get the quantity of a specific product in the cart
 */
export function getProductQuantityInCart(cart: Cart, productId: string): number {
  const line = getCartLineByProductId(cart, productId);
  return line ? line.quantity : 0;
}

/**
 * Check if cart can be loaded
 */
export function canLoadCart(): boolean {
  if(isAuthenticated() && getUser()?.storeCustomerId !== null) {
    return true;
  }

  return false;
}